import React, { useState, useEffect } from 'react';
import { useNotify } from 'react-admin';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  Chip,
  Autocomplete,
  Divider,
  Radio,
  RadioGroup,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CardMembership as CertificationIcon,
  CheckCircle as ValidIcon,
  Error as ExpiredIcon,
  Warning as ExpiringSoonIcon,
  Upload as UploadIcon,
  InsertDriveFile as InsertDriveFileIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

interface Certification {
  id: string;
  name: string;
  issuingOrganization: string;
  certificationNumber: string;
  issueDate: string;
  expiryDate: string;
  hasNoExpiry: boolean;
  description?: string;
  hasDocument?: boolean;
  documentFileId?: number;
  documentFile?: {
    id: number;
    url: string;
    name: string;
    size: number;
  };
}

interface CertificationsProps {
  data: Certification[];
  onChange: (data: Certification[]) => void;
}

interface CertificationType {
  id: number;
  name: string;
  langCode: string;
  isActive: boolean;
}

const Certifications: React.FC<CertificationsProps> = ({ data, onChange }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCertification, setEditingCertification] = useState<Certification | null>(null);
  const [formData, setFormData] = useState<Partial<Certification>>({});
  const [documentDialogOpen, setDocumentDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [isDragOver, setIsDragOver] = useState(false);
  const [certificationTypes, setCertificationTypes] = useState<CertificationType[]>([]);
  const [typesLoading, setTypesLoading] = useState(true);
  const notify = useNotify();

  // Load certification types from API
  const loadCertificationTypes = async () => {
    try {
      setTypesLoading(true);
      // Make a direct API call to the code-tables/certification-types endpoint
      const response = await fetch('http://localhost:3000/code-tables/certification-types', {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const types = await response.json();
        setCertificationTypes(types);
      } else {
        console.error('Failed to load certification types');
        notify('Failed to load certification types', { type: 'error' });
      }
    } catch (error) {
      console.error('Error loading certification types:', error);
      notify('Error loading certification types', { type: 'error' });
    } finally {
      setTypesLoading(false);
    }
  };

  useEffect(() => {
    loadCertificationTypes();
  }, []);

  const handleAdd = () => {
    setEditingCertification(null);
    setFormData({ hasNoExpiry: false });
    setDialogOpen(true);
  };

  const handleEdit = (certification: Certification) => {
    setEditingCertification(certification);
    setFormData(certification);
    setDialogOpen(true);
  };

  const handleDelete = (id: string) => {
    onChange(data.filter(certification => certification.id !== id));
  };

  const handleSave = () => {
    if (!formData.name || !formData.issuingOrganization) {
      return;
    }

    const certificationData: Certification = {
      id: editingCertification?.id || Date.now().toString(),
      name: formData.name!,
      issuingOrganization: formData.issuingOrganization!,
      certificationNumber: formData.certificationNumber || '',
      issueDate: formData.issueDate || '',
      expiryDate: formData.expiryDate || '',
      hasNoExpiry: formData.hasNoExpiry || false,
      description: formData.description || '',
    };

    if (editingCertification) {
      onChange(
        data.map(certification =>
          certification.id === editingCertification.id ? certificationData : certification
        )
      );
    } else {
      onChange([...data, certificationData]);
    }

    setDialogOpen(false);
    setFormData({});
    setEditingCertification(null);
  };

  const handleCancel = () => {
    setDialogOpen(false);
    setFormData({});
    setEditingCertification(null);
  };

  const updateFormData = (field: keyof Certification, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // Document upload handlers
  const handleDocumentUpload = () => {
    setDocumentDialogOpen(true);
    setPreviewUrl(formData.documentFile?.url || '');
  };

  const handleDocumentDialogClose = () => {
    setDocumentDialogOpen(false);
    setSelectedFile(null);
    setPreviewUrl(formData.documentFile?.url || '');
    setIsDragOver(false);
  };

  const validateFile = (file: File): boolean => {
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return false;
    }
    return true;
  };

  const handleFileSelect = (file: File) => {
    if (!validateFile(file)) {
      return;
    }

    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = e => {
      setPreviewUrl(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleDocumentSave = () => {
    if (selectedFile) {
      // In a real implementation, you would upload the file to a server here
      // For now, we'll simulate saving the document info
      const mockFileData = {
        id: Date.now(),
        url: previewUrl,
        name: selectedFile.name,
        size: selectedFile.size,
      };

      updateFormData('documentFile', mockFileData);
      updateFormData('documentFileId', mockFileData.id);
    }
    handleDocumentDialogClose();
  };

  const handleRemoveDocument = () => {
    updateFormData('documentFile', undefined);
    updateFormData('documentFileId', undefined);
    updateFormData('hasDocument', false);
    handleDocumentDialogClose();
  };

  const isImage = (filename?: string) => {
    if (!filename) {
      return false;
    }
    return /\.(jpg|jpeg|png|gif|svg|webp)$/i.test(filename);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) {
      return '';
    }
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
    });
  };

  const getCertificationStatus = (certification: Certification) => {
    if (certification.hasNoExpiry) {
      return { status: 'valid', label: 'No Expiry', color: 'success' };
    }

    if (!certification.expiryDate) {
      return { status: 'unknown', label: 'Unknown', color: 'default' };
    }

    const today = new Date();
    const expiryDate = new Date(certification.expiryDate);
    const daysUntilExpiry = Math.ceil(
      (expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (daysUntilExpiry < 0) {
      return { status: 'expired', label: 'Expired', color: 'error' };
    } else if (daysUntilExpiry <= 30) {
      return { status: 'expiring', label: 'Expires Soon', color: 'warning' };
    } else {
      return { status: 'valid', label: 'Valid', color: 'success' };
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'valid':
        return <ValidIcon />;
      case 'expired':
        return <ExpiredIcon />;
      case 'expiring':
        return <ExpiringSoonIcon />;
      default:
        return <CertificationIcon />;
    }
  };

  return (
    <Box>
      <Typography variant="body1" color="text.secondary" gutterBottom>
        This is where you add any certifications you hold, like CPR or Hazmat
      </Typography>

      <Stack spacing={3} mt={3}>
        {/* Add Certification Button */}
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          sx={{
            p: 2,
            borderStyle: 'dashed',
            borderColor: '#1976d2',
            color: '#1976d2',
            '&:hover': {
              borderStyle: 'dashed',
              borderColor: '#1565c0',
              backgroundColor: 'rgba(25, 118, 210, 0.04)',
            },
          }}
        >
          Add Certification
        </Button>

        {/* Certification Cards */}
        {data.map(certification => {
          const status = getCertificationStatus(certification);

          return (
            <Card key={certification.id} variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Stack direction="row" spacing={2} flex={1}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 1,
                        backgroundColor: '#e8f5e8',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <CertificationIcon sx={{ color: '#2e7d32' }} />
                    </Box>
                    <Box flex={1}>
                      <Typography variant="h6" fontWeight="600" gutterBottom>
                        {certification.name}
                      </Typography>
                      <Typography variant="body1" color="text.primary" gutterBottom>
                        {certification.issuingOrganization}
                      </Typography>

                      {certification.certificationNumber && (
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Certificate #{certification.certificationNumber}
                        </Typography>
                      )}

                      <Stack direction="row" spacing={2} alignItems="center" mb={1}>
                        {certification.issueDate && (
                          <Typography variant="body2" color="text.secondary">
                            Issued: {formatDate(certification.issueDate)}
                          </Typography>
                        )}
                        {!certification.hasNoExpiry && certification.expiryDate && (
                          <Typography variant="body2" color="text.secondary">
                            Expires: {formatDate(certification.expiryDate)}
                          </Typography>
                        )}
                      </Stack>

                      <Stack direction="row" spacing={1} alignItems="center" mt={1}>
                        <Chip
                          icon={getStatusIcon(status.status)}
                          label={status.label}
                          size="small"
                          color={status.color as any}
                          variant="filled"
                        />
                      </Stack>

                      {certification.description && (
                        <Typography variant="body2" color="text.secondary" mt={1}>
                          {certification.description}
                        </Typography>
                      )}
                      {certification.documentFile && (
                        <Stack direction="row" spacing={1} mt={1} alignItems="center">
                          <InsertDriveFileIcon sx={{ fontSize: 16, color: '#2e7d32' }} />
                          <Typography variant="caption" color="success.main">
                            Document uploaded
                          </Typography>
                        </Stack>
                      )}
                    </Box>
                  </Stack>
                  <Stack direction="row" spacing={1}>
                    <IconButton onClick={() => handleEdit(certification)} size="small">
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDelete(certification.id)}
                      size="small"
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          );
        })}

        {data.length === 0 && (
          <Card variant="outlined" sx={{ borderRadius: 2, borderStyle: 'dashed' }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <CertificationIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No certifications added yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Add any certifications you hold, like CPR or Hazmat
              </Typography>
            </CardContent>
          </Card>
        )}
      </Stack>

      {/* Add/Edit Certification Dialog */}
      <Dialog open={dialogOpen} onClose={handleCancel} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingCertification ? 'Edit Certification' : 'Add Certification'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <FormLabel sx={{ mb: 1 }}>* Certification Name</FormLabel>
                <Autocomplete
                  freeSolo
                  options={certificationTypes.map(type => type.name)}
                  value={formData.name || ''}
                  onChange={(event, newValue) => updateFormData('name', newValue)}
                  onInputChange={(event, newInputValue) => updateFormData('name', newInputValue)}
                  loading={typesLoading}
                  renderInput={params => (
                    <TextField {...params} placeholder="e.g., CPR Certification" fullWidth />
                  )}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <FormLabel sx={{ mb: 1 }}>* Issuing Organization</FormLabel>
                <TextField
                  value={formData.issuingOrganization || ''}
                  onChange={e => updateFormData('issuingOrganization', e.target.value)}
                  placeholder="e.g., Red Cross, Ministry of Transportation"
                  fullWidth
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <FormLabel sx={{ mb: 1 }}>Certification Number</FormLabel>
                <TextField
                  value={formData.certificationNumber || ''}
                  onChange={e => updateFormData('certificationNumber', e.target.value)}
                  placeholder="Certificate ID or number"
                  fullWidth
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <FormLabel sx={{ mb: 1 }}>Issue Date</FormLabel>
                <TextField
                  type="date"
                  value={formData.issueDate || ''}
                  onChange={e => updateFormData('issueDate', e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <FormLabel sx={{ mb: 1 }}>
                  <input
                    type="checkbox"
                    checked={formData.hasNoExpiry || false}
                    onChange={e => updateFormData('hasNoExpiry', e.target.checked)}
                    style={{ marginRight: 8 }}
                  />
                  This certification does not expire
                </FormLabel>
                {!formData.hasNoExpiry && (
                  <TextField
                    type="date"
                    value={formData.expiryDate || ''}
                    onChange={e => updateFormData('expiryDate', e.target.value)}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    placeholder="Expiry Date"
                  />
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <FormLabel sx={{ mb: 1 }}>Description (Optional)</FormLabel>
                <TextField
                  value={formData.description || ''}
                  onChange={e => updateFormData('description', e.target.value)}
                  placeholder="Additional details about this certification..."
                  multiline
                  rows={3}
                  fullWidth
                />
              </FormControl>
            </Grid>

            {/* Document Upload Section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <FormControl fullWidth>
                <FormLabel sx={{ mb: 2 }}>Do you have a Certification document?</FormLabel>
                <RadioGroup
                  value={
                    formData.hasDocument === true
                      ? 'true'
                      : formData.hasDocument === false
                      ? 'false'
                      : ''
                  }
                  onChange={e => {
                    const hasDoc = e.target.value === 'true';
                    updateFormData('hasDocument', hasDoc);
                    if (!hasDoc) {
                      updateFormData('documentFile', undefined);
                      updateFormData('documentFileId', undefined);
                    }
                  }}
                  row
                  sx={{ mb: 2 }}
                >
                  <FormControlLabel value="true" control={<Radio />} label="Yes" />
                  <FormControlLabel value="false" control={<Radio />} label="No" />
                </RadioGroup>

                {formData.hasDocument === true && (
                  <Box>
                    <FormLabel sx={{ mb: 2, display: 'block' }}>
                      Attach your certification document
                    </FormLabel>
                    <Button
                      variant="contained"
                      onClick={handleDocumentUpload}
                      startIcon={<UploadIcon />}
                      sx={{
                        backgroundColor: '#1976d2',
                        '&:hover': { backgroundColor: '#1565c0' },
                        mb: 2,
                      }}
                    >
                      {formData.documentFile
                        ? 'Replace Certification File'
                        : 'Add Certification File'}
                    </Button>

                    {formData.documentFile && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 2,
                          border: '1px solid #e0e0e0',
                          borderRadius: 1,
                          backgroundColor: '#f5f5f5',
                        }}
                      >
                        <Stack direction="row" spacing={2} alignItems="center">
                          <InsertDriveFileIcon sx={{ color: '#1976d2' }} />
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {formData.documentFile.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {(formData.documentFile.size / 1024 / 1024).toFixed(2)} MB
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    )}
                  </Box>
                )}
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!formData.name || !formData.issuingOrganization}
            sx={{
              backgroundColor: '#212121',
              '&:hover': { backgroundColor: '#424242' },
            }}
          >
            {editingCertification ? 'Update' : 'Add Certification'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Document Upload Dialog */}
      <Dialog open={documentDialogOpen} onClose={handleDocumentDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Certification Document
            <IconButton onClick={handleDocumentDialogClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', py: 2 }}>
            {previewUrl ? (
              isImage(selectedFile?.name || formData.documentFile?.name) ? (
                <Box
                  component="img"
                  src={previewUrl}
                  alt="Certification document preview"
                  sx={{
                    maxWidth: '100%',
                    maxHeight: 300,
                    borderRadius: 2,
                    mb: 2,
                  }}
                />
              ) : (
                <Box sx={{ mb: 2, p: 3, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                  <InsertDriveFileIcon sx={{ fontSize: 64, color: '#1976d2', mb: 1 }} />
                  <Typography variant="body1">
                    {selectedFile?.name || formData.documentFile?.name}
                  </Typography>
                </Box>
              )
            ) : (
              <Box
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => document.getElementById('certification-file-input')?.click()}
                sx={{
                  width: '100%',
                  height: 200,
                  border: isDragOver ? '2px dashed #1976d2' : '2px dashed #ccc',
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                  backgroundColor: isDragOver ? '#f3f7ff' : '#f9f9f9',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: '#1976d2',
                    backgroundColor: '#f3f7ff',
                  },
                }}
              >
                <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
                  <UploadIcon
                    sx={{ fontSize: 48, mb: 1, color: isDragOver ? '#1976d2' : 'inherit' }}
                  />
                  <Typography variant="body2" sx={{ color: isDragOver ? '#1976d2' : 'inherit' }}>
                    {isDragOver ? 'Drop file here' : 'Drag and drop file here or click to select'}
                  </Typography>
                </Box>
              </Box>
            )}

            <input
              accept="image/*,.pdf,.doc,.docx"
              style={{ display: 'none' }}
              id="certification-file-input"
              type="file"
              onChange={handleInputChange}
            />
            <label htmlFor="certification-file-input">
              <Button
                variant="outlined"
                component="span"
                startIcon={<InsertDriveFileIcon />}
                sx={{ mr: 1 }}
              >
                Select File
              </Button>
            </label>

            {formData.documentFile && (
              <Button
                variant="outlined"
                color="error"
                onClick={handleRemoveDocument}
                sx={{ ml: 1 }}
              >
                Remove Document
              </Button>
            )}

            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 2 }}>
              Maximum file size: 10MB • Supported formats: Images, PDF, DOC, DOCX
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDocumentDialogClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleDocumentSave}
            disabled={!selectedFile && !formData.documentFile}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Certifications;
